import React, { useMemo, useRef, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

const PALETTE = ["#ff5e7a", "#ffd54a", "#2eff7b", "#b04dff", "#4dc9ff", "#ff9d3c", "#ffffff"];

function useInstanceColors(meshRef, count) {
  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const c = new THREE.Color();
    for (let i = 0; i < count; i++) {
      mesh.setColorAt(i, c.set(PALETTE[i % PALETTE.length]));
    }
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }, [meshRef, count]);
}

// One-shot confetti explosion. Re-fires whenever `trigger` changes to a new truthy value.
export function ConfettiBurst({ trigger = 0, origin = [0, 3, -5], count = 180, spread = 6 }) {
  const mesh = useRef();
  const parts = useRef([]);
  const life = useRef(0);
  const lastTrigger = useRef(0);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  useInstanceColors(mesh, count);

  useEffect(() => {
    if (trigger && trigger !== lastTrigger.current) {
      lastTrigger.current = trigger;
      life.current = 2.4;
      parts.current = Array.from({ length: count }, () => ({
        p: new THREE.Vector3(origin[0], origin[1], origin[2]),
        v: new THREE.Vector3(
          (Math.random() - 0.5) * spread,
          Math.random() * spread * 0.9 + 2,
          (Math.random() - 0.5) * spread
        ),
        r: new THREE.Vector3(Math.random() * 6, Math.random() * 6, Math.random() * 6),
        rv: new THREE.Vector3(Math.random() * 8 - 4, Math.random() * 8 - 4, Math.random() * 8 - 4),
        s: 0.5 + Math.random() * 0.8,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  useFrame((_, dt) => {
    if (!mesh.current) return;
    if (life.current <= 0) {
      mesh.current.count = 0;
      return;
    }
    life.current -= dt;
    const fade = Math.min(1, life.current / 0.6);
    mesh.current.count = count;
    parts.current.forEach((pt, i) => {
      pt.v.y -= 7 * dt;
      pt.v.multiplyScalar(1 - 0.6 * dt);
      pt.p.addScaledVector(pt.v, dt);
      pt.r.addScaledVector(pt.rv, dt);
      dummy.position.copy(pt.p);
      dummy.rotation.set(pt.r.x, pt.r.y, pt.r.z);
      dummy.scale.setScalar(pt.s * fade * 0.14);
      dummy.updateMatrix();
      mesh.current.setMatrixAt(i, dummy.matrix);
    });
    mesh.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={mesh} args={[null, null, count]} frustumCulled={false}>
      <boxGeometry args={[1, 1, 0.12]} />
      <meshBasicMaterial toneMapped={false} />
    </instancedMesh>
  );
}

// Continuous celebratory rain for the results screen.
export function ConfettiRain({ active = true, count = 240, area = 18, height = 12 }) {
  const mesh = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const parts = useMemo(
    () =>
      Array.from({ length: count }, () => ({
        x: (Math.random() - 0.5) * area,
        y: Math.random() * height,
        z: (Math.random() - 0.5) * area - 2,
        vy: 1.2 + Math.random() * 1.8,
        phase: Math.random() * Math.PI * 2,
        rs: Math.random() * 4 + 1,
        s: 0.5 + Math.random() * 0.9,
      })),
    [count, area, height]
  );

  useInstanceColors(mesh, count);

  useFrame(({ clock }, dt) => {
    if (!mesh.current) return;
    if (!active) {
      mesh.current.count = 0;
      return;
    }
    mesh.current.count = count;
    const t = clock.elapsedTime;
    parts.forEach((pt, i) => {
      pt.y -= pt.vy * dt;
      if (pt.y < 0) pt.y = height;
      dummy.position.set(pt.x + Math.sin(t * 1.5 + pt.phase) * 0.6, pt.y, pt.z);
      dummy.rotation.set(t * pt.rs, t * pt.rs * 0.7, pt.phase);
      dummy.scale.setScalar(pt.s * 0.13);
      dummy.updateMatrix();
      mesh.current.setMatrixAt(i, dummy.matrix);
    });
    mesh.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={mesh} args={[null, null, count]} frustumCulled={false}>
      <boxGeometry args={[1, 1, 0.12]} />
      <meshBasicMaterial toneMapped={false} />
    </instancedMesh>
  );
}
